import React, {useMemo} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {isFontSize, ITypographyProps, VariantsMapping} from './ITypography';

import styles from './Typography.module.scss';

export const variantsAllowed: VariantsMapping = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
	h6: 'h6',
	p: 'p',
	div: 'div',
	span: 'span',
	ol: 'ol',
	ul: 'ul',
	li: 'li',
};

// The class generated for this component is globally, is contained inside scss/_typography.scss
const Typography = ({
	variant = 'p',
	classNames,
	style,
	children,
	fontSize = '16',
	fontWeight = 'regular',
	textCase = 'reg',
	sizes,
	onClick
}: ITypographyProps) => {
	const Component = variantsAllowed[variant] ?? 'p';

	const classes = useMemo(() => {
		const textCaseClasses = cn({
			[styles['text-transform--uppercase']]: textCase === 'upper',
			[styles['text-transform--capitalize']]: textCase === 'cap',
			[styles['text-transform--none']]: textCase === 'none',
			[styles['text-transform--lowercase']]: textCase === 'lower',
		});

		const queries = Object.keys(sizes || {});

		if (!queries.length) {
			const defaultClasses = `text-${fontWeight}-${fontSize}`;

			return cn(defaultClasses, textCaseClasses, classNames);
		}

		let responsiveClasses = '';

		queries.forEach(breakpoint => {
			const breakpointStyles = sizes[breakpoint];
			const [operator, operand] = ViewportMgr.parseQuery(breakpoint);
			let size = fontSize;
			let weight = fontWeight;

			const alias = ViewportMgr.getBreakpointsExtendedMap()[operand];

			if (isFontSize(breakpointStyles)) {
				size = breakpointStyles;
				weight = fontWeight;
			} else {
				size = breakpointStyles.fontSize;
				weight = breakpointStyles.fontWeight ?? fontWeight;
			}

			responsiveClasses = cn(
				responsiveClasses,
				{
					[styles[`text-${weight}-${size}-${alias}-greater`]]: operator === '>=',
					[styles[`text-${weight}-${size}-${alias}-greater-exclude`]]: operator === '>',
					[styles[`text-${weight}-${size}-${alias}-lesser`]]: operator === '<=',
					[styles[`text-${weight}-${size}-${alias}-lesser-exclude`]]: operator === '<',
				}
			);
		});

		return cn(responsiveClasses, textCaseClasses, classNames);
	}, [fontSize, fontWeight, textCase, sizes, classNames]);

	return (
		<Component onClick={onClick} className={classes} style={style}>
			{children}
		</Component>
	);
};

export default Typography;